<template>
  <div class="step-two">
    <!-- 选择签约企业主体 -->
    <SignCompany
      ref="signCompanyRef"
      :useExternalState="true"
      :externalStoreIds="selectedStoreIds"
      :enterpriseId="enterpriseId"
      :cooperationPlatform="cooperationPlatform"
      :disableEnterpriseSelect="false"
    />

    <!-- 选择门店 -->
    <div class="section-card">
      <div class="section-title">选择门店</div>
      <StoreSelect ref="storeSelectRef" @change="handleStoreChange" />
    </div>
  </div>
</template>

<script setup>
import { getSelectedStores, saveEntityAndStores } from '@/api/goods'
import { Message, MessageBox } from '@qs/ui'
import { onMounted, ref } from 'vue'
import SignCompany from '../editDrawer/components/SignCompany/index.vue'
import StoreSelect from './components/StoreSelect.vue'
import { useBatchSession } from './hooks/useBatchSession'
import { openAddStorePage } from './hooks/useStoreManagement'
import { quick } from '@/utils/ba'
import { trackClick } from '@/utils/trackReport'

const {
  sessionId,
  setEnterprise,
  enterpriseId,
  cooperationPlatform,
  setCooperationPlatform,
  setStoreSelectedIds,
} = useBatchSession()

const props = defineProps({
  source: {
    type: String,
    default: '',
  },
  trackId: {
    type: String,
    default: '',
  },
})
const storeSelectRef = ref(null)
const signCompanyRef = ref(null)

// 已选门店ID列表
const selectedStoreIds = ref([])

// 门店选择变更
const handleStoreChange = (storeIds) => {
  selectedStoreIds.value = storeIds || []
}

// 校验
const validate = async () => {
  // 校验签约主体选择
  await signCompanyRef.value?.validate()

  // 校验门店选择
  if (!storeSelectRef.value?.hasSelected?.()) {
    Message.error('请至少选择一个门店')
    throw new Error('请至少选择一个门店')
  }
}

// 获取数据
const getData = () => {
  return {
    storeIds: storeSelectRef.value?.getData() || [],
    ...signCompanyRef.value?.getData(),
  }
}

/**
 * 保存门店数据到服务端
 * @param {0 | 1} checkCity - 检查是否覆盖所有城市的门店，0-不检查，1-检查
 */
const saveStoreData = async (checkCity = 1) => {
  // 1. 获取数据
  const data = getData()

  // 2. 封装保存参数
  const saveData = {
    sessionId: sessionId.value,
    enterpriseId: data.enterpriseId,
    storeIds: data.storeIds,
    cooperationPlatform: data.cooperationPlatform || [],
    checkCity,
  }

  // 3. 调用接口保存
  const res = await saveEntityAndStores(saveData)
  if (res?.code !== 1) throw res

  // 4. 同步 Vuex
  setEnterprise(data.enterpriseId, null)
  setCooperationPlatform(data.cooperationPlatform || [])
}

/**
 * 尝试保存门店数据，如果部分城市无门店，让用户选择继续或新建门店
 * @throws 用户取消或选择新建门店时抛出异常
 */
const trySaveWithUserInteraction = async () => {
  try {
    // 尝试保存（检查门店覆盖所有城市）
    await saveStoreData(1)
  } catch (err) {
    // 如果部分城市无可用门店，让用户选择
    if (err?.code === 80001) {
      try {
        await MessageBox.confirm(err.msg, '部分城市无可用门店', {
          confirmButtonText: '继续上架',
          cancelButtonText: '新建门店',
          type: 'warning',
          customClass: 'confirm-box',
          distinguishCancelAndClose: true,
        })
        // 用户选择"继续上架" - 不检查门店，直接保存
        quick('click', 'commodity_batch_continue_publish', {
          source: props.source,
          trackId: props.trackId,
        })
        trackClick('commodity_batch_continue_publish', {
          source: props.source,
          trackId: props.trackId,
        })
        await saveStoreData(0)
      } catch (action) {
        if (action === 'close') {
          // 用户点击关闭按钮/ESC键/点击蒙层 - 什么都不做，抛出异常中断流程
          throw new Error('用户取消操作')
        }
        // 用户选择"新建门店" - 打开新建门店页面
        quick('click', 'commodity_batch_add_store', {
          source: props.source,
          trackId: props.trackId,
        })
        trackClick('commodity_batch_add_store', {
          source: props.source,
          trackId: props.trackId,
        })
        openAddStorePage()
        Message.info('请在新窗口中新建门店，完成后点击"下一步"继续')
        throw new Error('等待新建门店')
      }
    } else {
      // 其他错误，直接抛出
      throw err
    }
  }
}

// 下一步钩子
const onNext = async () => {
  await trySaveWithUserInteraction()
}

// 组件挂载时恢复状态
onMounted(async () => {
  // 从服务端获取已保存的签约主体和门店信息
  const res = await getSelectedStores({
    sessionId: sessionId.value,
  })

  if (res?.code === 1 && res.data) {
    const {
      enterpriseId: savedEnterpriseId,
      cooperationPlatform: savedPlatform,
      storeIds: savedStoreIds,
    } = res.data

    // 恢复数据后统一同步到 Vuex
    if (savedEnterpriseId) {
      setEnterprise(savedEnterpriseId, null)
      setCooperationPlatform(savedPlatform || [])
    }

    // 同步门店数据到 Vuex，StoreSelect 会自动从 Vuex 读取
    if (savedStoreIds?.length) {
      setStoreSelectedIds(savedStoreIds)
    }
  }
})

defineExpose({
  validate,
  getData,
  onNext,
})
</script>

<style lang="scss" scoped>
.step-two {
  // SignCompany 样式覆盖
  ::v-deep .sign-company {
    background: #fff;
    border: 1px solid #f2f3f5;
    border-radius: 8px;
    padding: 16px;
  }
}
</style>

<style lang="scss">
.confirm-box {
  .el-message-box__content {
    max-height: 300px;
    overflow-y: auto;
  }
}
</style>
